// Резултатите на голямата търсачка на листа „Инвентаризация" (изнесени от
// страницата): всяка дума е substring в името, по азбучен ред — до 30 продукта
// и до 15 рецепти, общо до 40 (както в изписването). Продуктът носи търсеното,
// последната доставна цена и дали вече е преброен; рецептата — дали вече е в
// „Приготвени рецепти".
import { useMemo } from "react";
import type { LastPrice, Lookup, Product } from "../../../../api/inventory";
import type { StocktakeSearchRow } from "./StocktakeWorksheetSearch";
import { isRowCounted, type StocktakeComputedRow } from "./stocktakeWorksheetShared";

const MAX_PRODUCTS = 30;
const MAX_RECIPES = 15;
const MAX_RESULTS = 40;

type Params = {
  products: Product[];
  recipes: Lookup[];
  // рецептите, вече добавени в „Приготвени рецепти"
  addedRecipeIds: ReadonlySet<number>;
  searchWords: string[];
  rowByProduct: Map<number, StocktakeComputedRow>;
  lastPriceByProduct: Map<number, LastPrice>;
  decimalsOf: (productId: number) => number;
};

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "bg");

export const useStocktakeSearchRows = ({
  products,
  recipes,
  addedRecipeIds,
  searchWords,
  rowByProduct,
  lastPriceByProduct,
  decimalsOf,
}: Params) => {
  const matches = useMemo(() => {
    if (!searchWords.length) return null;
    const matchesWords = (name: string) => {
      const haystack = name.toLowerCase();
      return searchWords.every((word) => haystack.includes(word));
    };
    return {
      products: products.filter((product) => matchesWords(product.name)).sort(byName).slice(0, MAX_PRODUCTS),
      recipes: recipes.filter((recipe) => matchesWords(recipe.name)).sort(byName).slice(0, MAX_RECIPES),
    };
  }, [products, recipes, searchWords]);

  const searchRows = useMemo<StocktakeSearchRow[]>(() => {
    if (!matches) return [];
    const productRows = matches.products.map((product): StocktakeSearchRow => {
      const row = rowByProduct.get(product.id);
      return {
        kind: "product",
        product,
        counted: !!row && isRowCounted(row.draft),
        systemQty: row?.systemQty ?? 0,
        unitPrice: lastPriceByProduct.get(product.id)?.unitPrice ?? null,
        decimals: decimalsOf(product.id),
      };
    });
    const recipeRows = matches.recipes.map(
      (recipe): StocktakeSearchRow => ({
        kind: "recipe",
        recipeId: recipe.id,
        name: recipe.name,
        added: addedRecipeIds.has(recipe.id),
      }),
    );
    return [...productRows, ...recipeRows].slice(0, MAX_RESULTS);
  }, [matches, rowByProduct, lastPriceByProduct, decimalsOf, addedRecipeIds]);

  return { searchRows };
};
